/**
 * Sample queries cycled next to the idle terminal cursor — the "what can I ask?" helper
 * (2026-05-23). Shown in white next to the blinking cursor when the prompt is empty; the
 * `<SampleQueryTicker>` rotates through them and the whole helper vanishes on first keystroke.
 *
 * These are deliberately corpus-agnostic — they showcase the *shapes* of question the agent's
 * MCP tool kit answers (synthesis, relationships, timelines, quotes, action items,
 * contradictions, recall) rather than naming specific notebooks, so they read sensibly for any
 * corpus.
 */
export const TERMINAL_SAMPLE_QUERIES: readonly string[] = [
  'What are my open action items across every project?',
  'Summarize everything I know about this quarter’s priorities.',
  'Which decisions did I make last month, and why?',
  'Trace how my thinking on this topic evolved over time.',
  'What contradicts what I wrote earlier about this plan?',
  'Find the quotes I saved about leadership.',
  'Who keeps coming up alongside this project in my notes?',
  'Build a timeline of everything related to the migration.',
  'What did I conclude in my last three meetings?',
  'Synthesize my notes on product strategy into one brief.',
  'What’s on my plate this week?',
  'Show me the relationships between these two initiatives.',
  'What open questions have I never resolved?',
  'Which notes mention budget concerns?',
  'What did I say I would follow up on but never did?',
  'Connect the dots between this person and that decision.',
  'Summarize the last conversation I had with the team.',
  'What recurring themes show up across my journal?',
  'Find every note where I changed my mind.',
  'What are the risks I’ve flagged but not mitigated?',
  'Pull together everything I know about this client.',
  'What deadlines are coming up based on my notes?',
  'Which ideas did I capture but never act on?',
  'What’s the history behind this naming decision?',
  'Show me contradictions between two stakeholders’ positions.',
  'What did past-me warn future-me about?',
  'Summarize my reading notes from this month.',
  'Who owns what across the active workstreams?',
  'What commitments did I make in writing?',
  'Trace the origin of this requirement.',
  'What have I learned about hiring?',
  'Find the strongest arguments I’ve recorded for this approach.',
  'What are the counterarguments I noted against it?',
  'Build a timeline of this product’s development.',
  'Which meetings produced concrete next steps?',
  'What’s unresolved between design and engineering?',
  'Summarize my one-on-one notes with this report.',
  'What patterns connect my recent project retrospectives?',
  'Surface notes I should revisit before the review.',
  'What did I plan to do differently next time?',
  'Show me everything tagged as a blocker.',
  'What feedback have I received more than once?',
  'Reconstruct the decision log for this feature.',
  'Which assumptions have I never validated?',
  'What did the data tell me last time I checked?',
  'Find quotes that capture my philosophy on this.',
  'Who introduced this idea, and when?',
  'What’s the throughline across my goal-setting notes?',
  'Summarize the debate I had with myself about this.',
  'What are my highest-leverage open tasks?',
  'Trace how this relationship developed over the year.',
  'What did I promise in the kickoff that’s still pending?',
  'Show me the evolution of this metric in my notes.',
  'What concerns did I raise that turned out to be right?',
  'Which projects have gone quiet in my notes?',
  'Summarize what I know about this competitor.',
  'What questions did the last meeting leave open?',
  'Find every mention of a hard deadline.',
  'What have I written about burnout or pace?',
  'Connect this experiment to its eventual outcome.',
  'What’s the strongest evidence for this hypothesis?',
  'Show me where two of my notes disagree.',
  'Summarize my notes from the offsite.',
  'What did I decide to deprioritize, and why?',
  'Which people do I owe a reply or follow-up?',
  'Build a timeline of this negotiation.',
  'What recurring obstacles show up across projects?',
  'Find notes about lessons learned.',
  'What’s the context behind this old decision?',
  'Summarize the arguments on both sides of this trade-off.',
  'What did I capture during my last brainstorm?',
  'Which goals from last quarter did I actually hit?',
  'Show me the dependencies between these tasks.',
  'What’s the earliest note I have on this topic?',
  'Find where I defined this term for myself.',
  'What did I want to remember about this book?',
  'Trace a single thread from idea to launch.',
  'What’s changed in my view since the last review?',
  'Summarize the status of every active project.',
  'Which notes contain numbers I should double-check?',
  'What did I flag as important but never expand on?',
  'Show me the people connected to this milestone.',
  'What contradictions exist in my requirements notes?',
  'Find the moment a project’s scope shifted.',
  'What advice have I given that I should take myself?',
  'Summarize my thinking on this in three bullets.',
  'What’s the relationship between these recurring topics?',
  'Which ideas keep resurfacing across months?',
  'What did I leave unfinished in my last work session?',
  'Build a timeline of my notes on this hire.',
  'What questions should I be asking that I’m not?',
  'Find the quotes I marked as worth remembering.',
  'What did I conclude after the last postmortem?',
  'Show me everything I know about this dependency.',
  'What commitments are at risk based on my notes?',
  'Summarize the case for and against this pivot.',
  'Which decisions am I revisiting too often?',
  'What’s the full backstory on this initiative?',
  'Find notes where my predictions were wrong.',
  'What should I prep before tomorrow’s meeting?',
];

/**
 * Pick `count` distinct sample queries at random — the chat idle state shows a small,
 * stable-per-mount selection as clickable prefill chips (U7 2026-07-05). Partial
 * Fisher–Yates over an index array; the pool is never mutated.
 */
export function pickSampleQueries(count: number): string[] {
  const indices = TERMINAL_SAMPLE_QUERIES.map((_, i) => i);
  const n = Math.min(count, indices.length);
  for (let i = 0; i < n; i += 1) {
    const j = i + Math.floor(Math.random() * (indices.length - i));
    [indices[i], indices[j]] = [indices[j], indices[i]];
  }
  return indices.slice(0, n).map((i) => TERMINAL_SAMPLE_QUERIES[i]);
}
